import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  ListItemText,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle,
  Event,
  Person,
  Info,
} from '@mui/icons-material';
import { useNotifications } from '@hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { NOTIFICATION_TYPES } from '@services/notificationService';

const NotificationsMenu = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.read) {
        await markAsRead(notification.notificationId);
      }
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }
      handleClose();
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleViewAll = () => {
    navigate('/notifications');
    handleClose();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.VOLUNTEER_APPROVED:
      case NOTIFICATION_TYPES.VOLUNTEER_PENDING:
      case NOTIFICATION_TYPES.VOLUNTEER_REJECTED:
        return <Person fontSize="small" color="primary" />;
      case NOTIFICATION_TYPES.EVENT_CREATED:
      case NOTIFICATION_TYPES.EVENT_UPDATED:
      case NOTIFICATION_TYPES.EVENT_CANCELLED:
      case NOTIFICATION_TYPES.ASSIGNMENT_CONFIRMED:
      case NOTIFICATION_TYPES.ASSIGNMENT_CANCELLED:
        return <Event fontSize="small" color="secondary" />;
      default:
        return <Info fontSize="small" color="action" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  // Get only recent notifications for the menu (max 5)
  const recentNotifications = notifications.slice(0, 5);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={handleOpen}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 360 },
            maxHeight: { xs: '80vh', sm: 480 },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight="bold">
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              startIcon={<CheckCircle />}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {/* Loading state */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* Empty state */}
        {!loading && notifications.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        )}

        {/* Notification list */}
        {!loading && recentNotifications.length > 0 && (
          <>
            {recentNotifications.map((notification) => (
              <MenuItem
                key={notification.notificationId}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 2,
                  px: 2,
                  minHeight: 48,
                  backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  '&:hover': {
                    backgroundColor: notification.read ? 'action.hover' : 'action.selected',
                  },
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                  <Box sx={{ mt: 0.5 }}>{getNotificationIcon(notification.type)}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={notification.read ? 'regular' : 'bold'}
                      sx={{ mb: 0.5 }}
                    >
                      {notification.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {formatTimestamp(notification.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
            <Divider />
            <Box sx={{ p: 1 }}>
              <Button fullWidth onClick={handleViewAll} sx={{ minHeight: 48 }}>
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationsMenu;
